import './LogDetailsFormModal.scss';
import React, { useState, useEffect } from 'react';
import { IonButton, IonButtons, IonContent, IonHeader, IonModal, IonTitle, IonToolbar } from '@ionic/react';
import { Formik, FormikProps, Field } from 'formik';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { GenericMessages } from '../../intl/GenericMessages';
import { JobMessages } from '../../intl/JobMessages';
import { logActions } from '../../redux/log/actions';
import { selectLogLocalDataForDetailUpdates } from '../../redux/log/selectors';
import * as yup from 'yup';
import { Input } from '../reusable-components/Input';
import { refreshAccessToken, getAuthorizedUser } from '../../ble/OAuth2DeviceAuth';

// **************************************************************************

const sentinelMaxApi = process.env.REACT_APP_SENTINEL_MAX_API;
const mqttGrantType = `${process.env.REACT_APP_MQTT_GRANT_TYPE}`;

// Sentinel Max Job and Job Segment interfaces
interface jobInterface {
  id: string;
  name: string;
  status: string;
}
interface jobInterfaceResponse {
  content: jobInterface[];
}

interface jobInterfaceFiltered {
  id: string;
  name: string;
}

// ------------------------------------

interface jobSegInterface {
  id: string;
  name: string;
  status: string;
}
interface jobSegInterfaceResponse {
  jobSegments: jobSegInterface[];
}

interface jobSegInterfaceFiltered {
  id: string;
  name: string;
}

// **************************************************************************

interface LogDetailsFormFields {
  logFileName: string;
  description: string;
  siteName?: string;
  heatExchangerModelNumber?: string;
  job: string;
  jobSegment: string;
}

export const LogDetailsFormModal: React.FC = () => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const jobThatNeedsDetails = useSelector(selectLogLocalDataForDetailUpdates);
  const hasJobThatNeedsDetails = Boolean(jobThatNeedsDetails);

  const [jobData, setJobData] = useState<jobInterfaceFiltered[]>([]);
  const [jobSegData, setJobSegData] = useState<jobSegInterfaceFiltered[]>([]);
  const [loading, setLoading] = useState(false);

  // **************************************************************************

  useEffect(() => {
    if (hasJobThatNeedsDetails) {
      const getJobsForDropdown = async () => {
        try {
          // Refresh access token
          await refreshAccessToken('refresh_token', mqttGrantType, localStorage.getItem('refreshToken') || '');

          // ----------------------------------------------------------------------

          // Get the organization for the logged on user
          let orgId = '';
          const objResponse1 = await getAuthorizedUser(localStorage.getItem('accessToken') || '');
          if (objResponse1) {
            orgId = objResponse1.organizationId;
          }

          // ----------------------------------------------------------------------

          // Get the job list for the logged on user's organization
          const url2 = new URL(`${sentinelMaxApi}/api-services/api/jobs/organization/` + orgId);
          const params = { page: '0', size: '5000' };
          //Append parameters to the URL
          url2.search = new URLSearchParams(params).toString();
          const response2 = await fetch(url2, {
            method: 'GET',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
            credentials: 'omit',
          });

          const data2: jobInterfaceResponse = await response2.json();
          if (!response2.ok) {
            throw new Error(`Error getting jobs for an organization | Status: ${response2.status}`);
          }

          //Transform and filter jobs
          const transformedData: jobInterfaceFiltered[] = data2.content
            .filter(job => job.status === 'IN_PROGRESS' || job.status === 'PENDING') // Selection (Filter)
            .map(job => ({
              // Transformation
              id: job.id,
              name: job.name,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)); // Sorts alphabetically by name

          //setJobData(data2.content);
          setJobData(transformedData);

          // ----------------------------------------------------------------------
        } catch (error) {
          console.error('API Call Error: ', error);
        } finally {
        }
      };
      getJobsForDropdown();
    }
  }, [hasJobThatNeedsDetails]);

  // **************************************************************************

  const onSubmission = async (info?: LogDetailsFormFields) => {
    // Return if no job that needs details exists.
    if (!jobThatNeedsDetails) {
      return;
    }

    if (!info) {
      if (!jobThatNeedsDetails?.filename) {
        console.error('Update attempted but no file name was provided.');
        return;
      }
      // Marks that the job details form was presented and sets defaults for metadata
      dispatch(
        logActions.updateLogInfo({
          filename: jobThatNeedsDetails.filename,
          metadata: undefined,
        }),
      );
      return;
    }

    if (info.job && info.jobSegment) {
      // Update Sentinel Max job/job segment status to IN_PROGRESS
      await updateSentinelMaxJobStatus(info.job, info.jobSegment);
    }
    console.log('Sentinel Max Job: ' + info.job + ' | JobSegment: ' + info.jobSegment + ' Selected');

    // Updates the job with details provided in the form.
    dispatch(
      logActions.updateLogInfo({
        filename: info.logFileName,
        metadata: {
          description: info.description,
          heatExchangerModelNumber: info.heatExchangerModelNumber || undefined,
          siteName: info.siteName || undefined,
        },
      }),
    );
  };

  const logDetailsValidationSchema = useMemo(() => {
    return yup.object().shape({
      description: yup.string().trim().required(formatMessage(GenericMessages.fieldRequiredLabel)),
    });
  }, [formatMessage]);

  return (
    <IonModal isOpen={!!jobThatNeedsDetails} initialBreakpoint={0.85} breakpoints={[0.85, 0.9]} backdropDismiss={false}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>
            <FormattedMessage {...JobMessages.jobDetailsFormTitle} />
          </IonTitle>
          <IonButtons slot="start">
            <IonButton onClick={() => onSubmission(undefined)}>
              <FormattedMessage {...GenericMessages.closeButtonPlaceholder} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {jobThatNeedsDetails?.filename && (
          <Formik
            initialValues={{
              logFileName: jobThatNeedsDetails.filename,
              siteName: '',
              description: jobThatNeedsDetails.filename.replace(/\.log$/i, ''),
              heatExchangerModelNumber: '',
              job: '',
              jobSegment: '',
            }}
            onSubmit={(values: LogDetailsFormFields) => onSubmission(values)}
            enableReinitialize={true}
            validationSchema={logDetailsValidationSchema}
          >
            {({
              values,
              setFieldValue,
              handleChange,
              handleSubmit,
              errors,
              touched,
            }: FormikProps<LogDetailsFormFields>) => (
              <div className="job-details-log-details-form-row">
                <form onSubmit={handleSubmit}>
                  <input style={{ display: 'none' }} autoFocus />
                  <Input
                    label={formatMessage(JobMessages.uploadFormLogFileLabel)}
                    value={values.logFileName}
                    disabled={true}
                    name="logFileName"
                    id="log-details-log-file-input"
                  />
                  <Input
                    label={formatMessage(JobMessages.uploadFormDescriptionLabel)}
                    value={values.description}
                    placeholder={formatMessage(JobMessages.uploadFormDescriptionPlaceholder)}
                    onChange={handleChange}
                    name="description"
                    id="log-details-description-input"
                    errorText={touched.description ? errors?.description : undefined}
                    required
                  />
                  <Input
                    label={formatMessage(JobMessages.uploadFormSiteNameLabel)}
                    value={values.siteName}
                    placeholder={formatMessage(JobMessages.uploadFormSiteNamePlaceholder)}
                    onChange={handleChange}
                    name="siteName"
                    id="log-details-site-name-input"
                  />
                  <Input
                    label={formatMessage(JobMessages.uploadFormHXModelNumberLabel)}
                    value={values.heatExchangerModelNumber}
                    placeholder={formatMessage(JobMessages.uploadFormHXModelNumberPlaceholder)}
                    onChange={handleChange}
                    name="heatExchangerModelNumber"
                    id="log-details-hx-model-number-input"
                  />

                  <div className="custom-input-label-row">
                    <label htmlFor="job" className="custom-input-label sc-ion-label-md-h sc-ion-label-md-s md">
                      Sentinel Max Job
                    </label>
                  </div>

                  <div
                    id="jobDiv"
                    className="outlined-input-item item-interactive item-input item-has-placeholder item-has-value item md item-fill-outline job-div"
                  >
                    <div className="item-native">
                      <Field
                        name="job"
                        id="job"
                        as="select"
                        className="native-input sc-ion-input-md job-dropdown"
                        value={values.job}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          const selectedJob = e.target.value;
                          setFieldValue('job', selectedJob);
                          setFieldValue('jobSegment', '');
                          if (selectedJob) {
                            getJobSegmentsForDropdown(selectedJob, setJobSegData, setLoading);
                          } else {
                            setJobSegData([]);
                          }
                        }}
                      >
                        <option value="">Select a Job</option>
                        {jobData.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </Field>
                    </div>
                  </div>

                  <div className="custom-input-label-row">
                    <label htmlFor="jobSegment" className="custom-input-label sc-ion-label-md-h sc-ion-label-md-s md">
                      Sentinel Max Job Segment
                    </label>
                  </div>
                  <div className="outlined-input-item item-interactive item-input item-has-placeholder item-has-value item md item-fill-outline job-segment-div">
                    <Field
                      name="jobSegment"
                      id="jobSegment"
                      as="select"
                      className="native-input sc-ion-input-md job-dropdown"
                      value={values.jobSegment}
                      disabled={!values.job || loading}
                    >
                      <option value="">{loading ? 'Loading...' : 'Select a Job Segment'}</option>
                      {jobSegData.map(option => (
                        <option key={option.id} value={option.id}>
                          {option.name}
                        </option>
                      ))}
                    </Field>
                  </div>
                  <div className="job-details-footer">
                    <IonButton className="job-details-upload-button" expand="block" size="large" type="submit">
                      <FormattedMessage {...GenericMessages.submitButtonLabel} />
                    </IonButton>
                  </div>
                </form>
              </div>
            )}
          </Formik>
        )}
      </IonContent>
    </IonModal>
  );
};

// **************************************************************************

const getJobSegmentsForDropdown = async (
  jobId: string,
  setJobSegData: (data: jobSegInterfaceFiltered[]) => void,
  setLoading: (l: boolean) => void,
) => {
  setLoading(true);
  try {
    // Refresh access token
    await refreshAccessToken('refresh_token', mqttGrantType, localStorage.getItem('refreshToken') || '');

    // ----------------------------------------------------------------------

    // Get the job segments for the logged on user's job selected
    const url = new URL(`${sentinelMaxApi}/api-services/api/jobs/${jobId}/with-job-segments`);
    const response2 = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
      credentials: 'omit',
    });

    const data: jobSegInterfaceResponse = await response2.json();
    if (!response2.ok) {
      if (response2.status === 500) {
        console.log('No segments available');
        setLoading(false);
        return;
      } else {
        throw new Error(`Status: ${response2.status}`);
      }
    }

    //Transform and filter jobs
    const transformedData: jobSegInterfaceFiltered[] = data.jobSegments
      .filter(job => job.status === 'IN_PROGRESS' || job.status === 'PENDING') // Selection (Filter)
      .map(job => ({
        // Transformation
        id: job.id,
        name: job.name,
      }))
      .sort((a, b) => a.name.localeCompare(b.name)); // Sorts alphabetically by name

    setJobSegData(transformedData);
    setLoading(false);

    // ----------------------------------------------------------------------
  } catch (error) {
    console.error('Error accessing job segments', error);
    setJobSegData([]);
    setLoading(false);
  }
};

// **************************************************************************

const updateSentinelMaxJobStatus = async (jobId: string, jobSegmentId: string) => {
  try {
    // Refresh access token
    await refreshAccessToken('refresh_token', mqttGrantType, localStorage.getItem('refreshToken') || '');

    // ----------------------------------------------------------------------

    // Update job status to IN_PROGRESS
    const response = await fetch(`${sentinelMaxApi}/api-services/api/jobs/${jobId}/start`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
      credentials: 'omit',
    });

    const data = await response.json();
    if (!response.ok) {
      if (response.status === 400) {
        console.log('Job already in IN_PROGRESS status');
      } else {
        throw new Error(
          `Status: ${response.status} | Error: ${data.error} | Error Description: ${data.error_description}`,
        );
      }
    }

    // ----------------------------------------------------------------------

    // Update job segment status to IN_PROGRESS
    const reqBody = `{"startTime": "` + new Date().toISOString() + `", "status": "IN_PROGRESS"}`;

    const response2 = await fetch(`${sentinelMaxApi}/api-services/api/job-segments/${jobSegmentId}`, {
      method: 'PATCH',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
      credentials: 'omit',
      body: reqBody,
    });

    const data2 = await response2.json();
    if (response2.ok) {
      // set localstorage values to trigger MQTT transfer
      localStorage.setItem('sentinelMaxJobId', jobId);
      localStorage.setItem('sentinelMaxJobSegmentId', jobSegmentId);
      console.log(`*** Set Job/Segment: ${jobId} / ${jobSegmentId}`);
    } else {
      throw new Error(
        `Status: ${response2.status} | Error: ${data2.error} | Error Description: ${data2.error_description}`,
      );
    }
  } catch (error) {
    console.error('Error in updateSentinelMaxJobStatus: ', error);
  }
};
